/**
 * Quick Select Dropdown Component
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tags, FileCheck, FileX, Clock, FolderOpen, ChevronDown, X } from 'lucide-react';

export const QuickSelectMenu = ({
  onSelectAll,
  onSelectWithContent,
  onSelectEmpty,
  onSelectRecent,
  onSelectByFolder,
  onClearSelection,
  folders = [],
  folderCounts = {},
  counts = {},
  hasSelection = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (action) => {
    action();
    setIsOpen(false);
  };

  return (
    <div className="relative" data-testid="quick-select-menu">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-secondary hover:bg-secondary/80 transition-all"
        data-testid="quick-select-btn"
      >
        Quick Select
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute left-0 top-full mt-1 bg-card border border-border rounded-xl shadow-xl py-2 min-w-[200px] z-50"
          >
            {/* By Status */}
            <div className="px-3 py-1 text-xs text-muted-foreground font-medium">By Status</div>
            
            <button
              onClick={() => handleSelect(onSelectAll)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
            >
              <Tags className="w-4 h-4 text-muted-foreground" />
              All visible ({counts.all || 0})
            </button>
            
            <button
              onClick={() => handleSelect(onSelectWithContent)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
            >
              <FileCheck className="w-4 h-4 text-green-500" />
              All with content ({counts.withContent || 0})
            </button>
            
            <button
              onClick={() => handleSelect(onSelectEmpty)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
            >
              <FileX className="w-4 h-4 text-gray-400" />
              All empty ({counts.empty || 0})
            </button>
            
            <button
              onClick={() => handleSelect(onSelectRecent)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
            >
              <Clock className="w-4 h-4 text-blue-500" />
              Recent (last 7 days)
            </button>
            
            {/* By Folder */}
            {folders.length > 0 && (
              <>
                <div className="border-t border-border my-1" />
                <div className="px-3 py-1 text-xs text-muted-foreground font-medium">By Folder</div>
                {folders.map(folder => (
                  <button
                    key={folder}
                    onClick={() => handleSelect(() => onSelectByFolder(folder))}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <FolderOpen className="w-4 h-4 text-muted-foreground" />
                    {folder} ({folderCounts[folder] || 0})
                  </button>
                ))}
              </>
            )}
            
            {/* Clear Selection */}
            {hasSelection && (
              <>
                <div className="border-t border-border my-1" />
                <button
                  onClick={() => handleSelect(onClearSelection)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 text-muted-foreground"
                >
                  <X className="w-4 h-4" />
                  Clear selection
                </button>
              </>
            )}
          </motion.div>
        </>
      )}
    </div>
  );
};

export default QuickSelectMenu;
